import forEachElement from '../../elements/forEachElement';
import * as panelModel from './panel';
import * as stepModel from './step';
import * as eventsModel from './events';
import { classes, selectors } from '../elements/constants';
import getFormWizard from '../elements/getFormWizard';
import getActivePanel from '../elements/getActivePanel';
import getForm from '../elements/getForm';

/**
 * Cancels fallback onclick actions on each next button in an element
 * (eg. form wizard, form, or panel)
 *
 * @param {Element} element
 */
export const cancelFallbackOnclickActions = element => {
  const nextButtons = panelModel.getNextButtons(element);
  forEachElement(nextButtons, nextButton => {
    nextButton.removeAttribute('onclick');
  });
};

/**
 * Handles `formWizard.navigate` events called from form steps, and performs the main navigation.
 *
 * @param {Event} event
 * @param {Element} event.target - The button or step that initiated the navigation.
 * @param {string} [event.detail.toStep] - The specific step you wish to navigate to
 * @param {string} [event.detail.direction] - The direction ('forwards' or 'backwards')
 */
export function navigate(event) {
  // validation or other handlers could have cancelled navigation
  if(event.defaultPrevented) { return; }
  navigateFromStep(event);
  navigateToStep(event);
}

/**
 * Navigates away from the current step, based on the target and detail of the navigation event.
 */
function navigateFromStep(event) {
  const fromStep = stepModel.getStep(event.target);

  // tell the current/previous step it is complete and can 'hide' itself
  if(fromStep && event.detail.direction !== 'backwards') {
    eventsModel.dispatchCompleteStepEvent(fromStep);
  }
}

/**
 * Navigates to the next step, based on the target and detail of the navigation event.
 */
function navigateToStep(event) {
  const fromPanel = getActivePanel(getForm(event.target));
  const nextStep = getNavigationNextStep(event);

  // tell the next step it can 'show' itself, whatever that looks like
  // unless there is no next step, in which case nothing happens
  if(nextStep) {
    eventsModel.dispatchShowStepEvent(nextStep, { direction: event.detail.direction, fromPanel });
    if(nextStep.classList.contains(classes.flexPanel)) {
      const form = getFormWizard(event.target);
      const stepIndex = [...form.querySelectorAll(selectors.step)].indexOf(nextStep);
      if(form && stepIndex > -1) {
        updateFormStepIndex(form, stepIndex);
      }
    }
  }
}

/**
 * @param {CustomEvent} event - The formWizard.navigate event
 * @returns {Element} The step that we should be navigating to
 */
export function getNavigationNextStep({ target, detail }) {
  let nextStep = detail.toStep || stepModel.getNextStep(target);
  const fromStep = stepModel.getStep(target);

  if(detail.direction !== 'backwards') {
    // by default (i.e. if no explicit target specified), nextStep is literally the next step
    if(!nextStep && fromStep) { nextStep = fromStep.nextElementSibling; }
  }

  return nextStep;
}

/**
 * Forward panel navigation handler for progressing the leadform to the next panel
 *
 * @param {Event} event - Event coming from next button or panel itself
 */
export function navigateForwards(event) {
  const { target } = event;
  const step = stepModel.getStep(target);

  if (!target || !step || target.getAttribute('type') === 'submit') { return; }
  if (step.querySelector(classes.buttonLoading)) { return; }

  eventsModel.dispatchNavigateEvent(target, { direction: 'forwards' });
}

/**
 * Backwards panel navigation handler for progressing the leadform to the previous panel
 *
 * @param {Event} event - Event coming from back button
 */
export function navigateBackwards(event) {
  const { target } = event;
  const step = stepModel.getStep(target);
  if (!target || !step || step.querySelector(classes.buttonLoading)) { return; }

  eventsModel.dispatchNavigateEvent(target, { direction: 'backwards' });
}

function updateFormStepIndex(formWizard, stepIndex) {
  formWizard.dataset.stepIndex = stepIndex;

  // Update window push state
  if (!formWizard.dataset.pushState || !window.history.pushState) { return; }

  const hashValue = !stepIndex ? '' : `#get-started/${stepIndex}`;
  if (window.location.hash === hashValue) { return; }
  window.history.pushState({ stepIndex, formWizardId: formWizard.id }, null, hashValue);
}

/**
 * Responds to popState events on the window, to navigate the form using back/forward buttons.
 */
export function popState(event) {
  const state = event.state || window.history.state || {};
  const stepIndex = state.stepIndex ? parseInt(state.stepIndex) : 0;
  const formWizard = state.formWizardId ?
    document.getElementById(state.formWizardId) :
    document.querySelector(selectors.wrapper);

  if (formWizard && formWizard.dataset.pushState) {
    moveFormToStepIndex(formWizard, stepIndex);
  }
}

function moveFormToStepIndex(formWizard, stepIndex) {
  // Find step to move to and from
  const fromStep = getActivePanel(formWizard);
  const toStep = [...formWizard.querySelectorAll(selectors.step)][stepIndex];
  const currentStepIndex = parseInt(formWizard.dataset.stepIndex);
  const direction = currentStepIndex > stepIndex ? 'backwards' : 'forwards';

  if (!fromStep || !toStep || fromStep === toStep) return;

  // Programatically navigate to the required form step
  eventsModel.dispatchNavigateEvent(fromStep, { toStep, direction });
}
